'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Clock3 } from 'lucide-react';
import { Box, Divider, Drawer, IconButton, List, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';

type LinkItem = {
  title?: string;
  url?: string;
  separator?: boolean;
  soon?: boolean;
};

type Section = {
  title: string;
  links: LinkItem[];
};

const MobileLinkLabel = ({ link }: { link: LinkItem }) => (
  <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {link.title}
    {link.soon ? (
      <Box
        component='span'
        title='Em breve'
        aria-label='Em breve'
        sx={{ display: 'inline-flex', color: 'text.secondary' }}
      >
        <Clock3 size={14} strokeWidth={2.1} aria-hidden='true' />
      </Box>
    ) : null}
  </Box>
);

interface MobileNavProps {
  // accepts either a flat list of links or an array of sections
  links: LinkItem[] | Section[];
  desktopBreakpoint?: 'md' | 'lg';
}

export const MobileNav = ({ links, desktopBreakpoint = 'lg' }: MobileNavProps) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  return (
    <>
      <IconButton
        aria-label='Abrir menu'
        onClick={() => setIsSheetOpen(true)}
        size='small'
        sx={{
          display: desktopBreakpoint === 'md' ? { md: 'none' } : { lg: 'none' },
        }}
      >
        <svg width='14' height='14' fill='currentColor' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'>
          <title>Menu</title>
          <path d='M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z' />
        </svg>
      </IconButton>
      <Drawer
        anchor='left'
        open={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
        PaperProps={{
          sx: {
            width: 'min(88vw, 320px)',
            maxWidth: '100vw',
            height: '100dvh',
            boxSizing: 'border-box',
            overflowY: 'auto',
          },
        }}
      >
        <Box sx={{ width: '100%', minHeight: '100%', p: 2 }}>
          <Stack direction='row' spacing={1.25} alignItems='center'>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.25,
                border: '1px solid hsl(var(--border))',
                background: 'linear-gradient(160deg, hsl(var(--background)) 0%, hsl(var(--muted)) 100%)',
                boxShadow: '0 4px 12px hsl(var(--foreground) / 0.08)',
                overflow: 'hidden',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Box
                component='img'
                src='/logo192_transpbg.png'
                alt='Logo Análise de FIIs'
                sx={{
                  width: 32,
                  height: 32,
                  objectFit: 'contain',
                  transform: 'scale(1.28)',
                }}
              />
            </Box>
            <Typography variant='subtitle1' fontWeight={700}>
              <Box component='span' sx={{ fontFamily: 'var(--font-display)', letterSpacing: 0.2 }}>
                Análise de FIIs
              </Box>
            </Typography>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
            {Array.isArray(links) && links.length > 0 && 'links' in links[0]
              ? (links as Section[]).map((section) => (
                  <Box key={section.title} sx={{ pb: 1 }}>
                    <Typography variant='overline' sx={{ px: 1, color: 'text.secondary' }}>
                      {section.title}
                    </Typography>
                    <List dense>
                      {section.links.map((link, idx) =>
                        (link as any).separator ? (
                          <Divider key={`sep-${idx}`} sx={{ my: 1 }} />
                        ) : (
                          <ListItemButton
                            key={(link as any).title}
                            component={Link}
                            href={(link as any).url}
                            onClick={() => setIsSheetOpen(false)}
                          >
                            <ListItemText primary={<MobileLinkLabel link={link as LinkItem} />} />
                          </ListItemButton>
                        ),
                      )}
                    </List>
                  </Box>
                ))
              : (links as LinkItem[]).map((link) => (
                  <ListItemButton
                    key={(link as any).title}
                    component={Link}
                    href={(link as any).url}
                    onClick={() => setIsSheetOpen(false)}
                  >
                    <ListItemText primary={<MobileLinkLabel link={link} />} />
                  </ListItemButton>
                ))}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
